import {
  FC, memo, useCallback, useEffect, useState,
} from 'react';
import { createUseStyles } from 'react-jss';
import cn from 'classnames';
import { createPortal } from 'react-dom';

interface TPoint {
  x: number,
  y: number,
}

const getElementPosition = (selector: string, defaultValue: TPoint): TPoint => {
  if (typeof document === 'undefined') {
    return defaultValue;
  }
  const button = document.querySelector(selector);
  if (!button) {
    return defaultValue;
  }
  const { x, y } = button.getBoundingClientRect();
  return { x, y };
};

interface FilterProps {
  className?: string,
  disabled?: boolean,
  items: {
    value: string,
    label: string,
  }[],
  values: string[],
  onChange?: (values: string[]) => void,
}

const Filter: FC<FilterProps> = (props) => {
  const {
    className,
    disabled,
    items,
    values,
    onChange,
  } = props;

  const [isOpened, setOpened] = useState(false);
  const [isMounted, setMounted] = useState(false);
  const [isShown, setShown] = useState(false);
  const [tempValues, setTempValues] = useState([...values]);
  const [position, setPosition] = useState({ x: 0, y: 0 });

  const isMobile = typeof document !== 'undefined' && document.body.clientWidth <= 1024;

  const classes = useStyles();

  const handleOpen = useCallback(() => {
    setPosition(getElementPosition(`.${classes.container} > button`, { x: 0, y: 0 }));
    setOpened(true);
  }, [classes.container]);

  const handleClose = useCallback(() => {
    setTempValues([...values]);
    setOpened(false);
  }, [values]);

  const handleClear = useCallback(() => {
    setTempValues([]);
    onChange?.([]);
    setOpened(false);
  }, [onChange]);

  const handleTempChange = useCallback((value: string) => {
    if (tempValues.includes(value)) {
      setTempValues([...tempValues.filter((item) => item !== value)]);
    }
    if (!tempValues.includes(value)) {
      setTempValues([...tempValues, value]);
    }
  }, [tempValues]);

  const handleChange = useCallback(() => {
    if (onChange && JSON.stringify(tempValues) !== JSON.stringify(values)) {
      onChange(tempValues);
    }
    setOpened(false);
  }, [onChange, tempValues, values]);

  const handleResize = useCallback(() => {
    setPosition(getElementPosition(`.${classes.container} > button`, { x: 0, y: 0 }));
  }, [classes.container]);

  const handleClick = useCallback((event: any) => {
    if (event.target.closest(`.${classes.container}`) || event.target.closest(`.${classes.dropdown}`)) {
      return;
    }
    handleClose();
  }, [classes.container, classes.dropdown, handleClose]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return undefined;
    }
    window.addEventListener('resize', handleResize);
    document.addEventListener('click', handleClick);
    return () => {
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('click', handleClick);
    };
  }, [handleClick, handleResize]);

  useEffect(() => {
    if (isOpened && !isShown) {
      setMounted(true);
      setTimeout(() => {
        setShown(true);
      }, 10);
    }
    if (!isOpened && isShown) {
      setShown(false);
      setTimeout(() => {
        setMounted(false);
      }, 300);
    }
  }, [isMounted, isOpened, isShown]);

  const dropdownStyles = {
    top: !isMobile && position.y ? `${position.y}px` : undefined,
    left: !isMobile && position.x ? `${position.x}px` : undefined,
  };

  return (
    <>
      <div className={cn(className, classes.container, { disabled })}>
        {values.length > 0 && (
          <span>{values.length}</span>
        )}
        <button
          type="button"
          role="open"
          onClick={handleOpen}
          disabled={disabled}
        >
          Filter
        </button>
      </div>
      {isMounted && createPortal(
        <div className={cn(classes.dropdown, { [classes.isDropdownShown]: isShown })} style={dropdownStyles}>
          <div className={classes.header}>
            <button type="button" role="close" onClick={handleClose}>
              <svg viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M22.0703 22.0712L7.92818 7.92903" stroke="white" strokeWidth="2" strokeLinecap="round" />
                <path d="M7.92969 22.0712L22.0718 7.92903" stroke="white" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>
            <div>Filter</div>
            <button type="button" role="clear" onClick={handleClear}>Clear</button>
          </div>
          <div className={classes.content}>
            <div className={classes.items}>
              {items.map((item, key) => (
                <button
                  key={key/* item.value */}
                  type="button"
                  role="item"
                  onClick={handleTempChange.bind(null, item.value)}
                  is={tempValues.includes(item.value) ? 'selected' : undefined}
                >
                  <span>
                    <svg viewBox="0 0 12 10" xmlns="http://www.w3.org/2000/svg">
                      <path d="M1.70711 4.69289C1.31658 4.30237 0.683417 4.30237 0.292893 4.69289C-0.0976311 5.08342 -0.0976311 5.71658 0.292893 6.10711L1.70711 4.69289ZM11.0192 1.57346C11.3359 1.12101 11.2259 0.497483 10.7735 0.180768C10.321 -0.135946 9.69748 -0.0259116 9.38077 0.426538L11.0192 1.57346ZM3.75718 8.15718L3.05008 8.86429L3.75718 8.15718ZM5.28352 8.02354L4.46429 7.45008L5.28352 8.02354ZM0.292893 6.10711L3.05008 8.86429L4.46429 7.45008L1.70711 4.69289L0.292893 6.10711ZM6.10275 8.597L11.0192 1.57346L9.38077 0.426538L4.46429 7.45008L6.10275 8.597ZM3.05008 8.86429C3.92944 9.74365 5.3896 9.6158 6.10275 8.597L4.46429 7.45008H4.46429L3.05008 8.86429Z" />
                    </svg>
                  </span>
                  {item.label}
                </button>
              ))}
            </div>
            <div className={classes.footer}>
              <button
                type="button"
                role="submit"
                onClick={handleChange}
              >
                Show results
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
};

interface StyleProps {
  position: TPoint,
}

const useStyles = createUseStyles<string, unknown, StyleProps>({
  container: {
    '&': {
      display: 'flex',
    },
    '& [role="open"]': {
      '&': {
        position: 'relative',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        border: '1px solid #a4a4a4',
        borderRadius: '0.4rem',
        fontSize: '1.2rem',
        lineHeight: '1rem',
        textAlign: 'center',
        fontWeight: 400,
        padding: '0.0rem 1.5rem 0.0rem 1.5rem',
        background: 'transparent',
        color: '#585858',
        transition: '300ms background ease, 300ms color ease, 300ms border-color ease',
        cursor: 'pointer',
        zIndex: 1,
      },
      '&[disabled]': {
        background: '#f5f5f5',
        color: '#868686',
        borderColor: '#aaaaaa',
        pointerEvents: 'none',
      },
      '@media (min-width: 375px)': {
        fontSize: '1.4rem',
        lineHeight: '1.4rem',
      },
    },
    '& span': {
      position: 'relative',
      display: 'flex',
      width: '1.7rem',
      height: '1.7rem',
      background: '#39C4BD',
      borderRadius: '50%',
      color: '#ffffff',
      justifyContent: 'center',
      alignItems: 'center',
      fontSize: '1.2rem',
      fontWeight: 800,
      right: '-0.9rem',
      top: '-0.5rem',
      zIndex: 2,
    },
  },
  dropdown: {
    '&': {
      position: 'absolute',
      display: 'flex',
      flexDirection: 'column',
      background: '#ffffff',
      top: 0,
      left: 0,
      bottom: 0,
      right: 0,
      opacity: 0,
      transition: '300ms opacity ease',
      zIndex: 20,
    },
    '@media (min-width: 1025px)': {
      bottom: 'unset',
      right: 'unset',
      width: '40rem',
      height: '55rem',
      borderRadius: '0.7rem',
      overflow: 'hidden',
      transform: 'translate(calc(-100% + 6.4rem), 3.5rem)',
      boxShadow: '0 0 1rem rgba(0, 0, 0, 0.3)',
    },
    '& [role="close"]': {
      '&': {
        border: 0,
        background: 'none',
        margin: '0 0 0 0.2rem',
        padding: '1rem',
        width: '5rem',
        height: '5rem',
        zIndex: 2,
      },
      '@media (min-width: 1025px)': {
        visibility: 'hidden',
        pointerEvents: 'none',
      },
    },
    '& [role="clear"]': {
      border: 0,
      background: 'none',
      margin: '0 1rem 0 0',
      padding: '1rem',
      fontSize: '1.6rem',
      fontWeight: 600,
      color: 'inherit',
      zIndex: 2,
    },
    '& [role="submit"]': {
      background: '#39C4BD',
      padding: '1rem 3.2rem',
      // border: '2px solid #ffffff',
      border: 0,
      borderRadius: '2.6rem',
      fontSize: '2rem',
      lineHeight: '2.8rem',
      fontWeight: 500,
      color: '#ffffff',
      pointerEvents: 'all',
      boxShadow: '0 0 2rem rgba(0,0,0,0.2)',
    },
  },
  isDropdownShown: {
    opacity: 1,
  },
  header: {
    '&': {
      position: 'relative',
      display: 'flex',
      height: '6rem',
      background: '#39C4BD',
      fontSize: '2rem',
      fontWeight: 500,
      color: '#ffffff',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    '& div': {
      position: 'absolute',
      display: 'flex',
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
      justifyContent: 'center',
      alignItems: 'center',
    },
  },
  content: {
    position: 'relative',
    flex: 1,
  },
  items: {
    '&': {
      position: 'absolute',
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
      overflow: 'auto',
      paddingBottom: '9rem',
    },
    '& [role="item"]': {
      '&': {
        display: 'flex',
        width: '100%',
        height: '6rem',
        border: 0,
        borderBottom: '1px solid #F2F2F2',
        color: '#3F3F3F',
        background: 'transparent',
        transition: '300ms opacity ease',
        fontSize: '1.8rem',
        justifyContent: 'flex-start',
        alignItems: 'center',
        fontWeight: 400,
        fontFamily: 'inherit',
        paddingLeft: '1.6rem',
      },
      '& span': {
        display: 'flex',
        width: '2.4rem',
        height: '2.4rem',
        border: '0.1rem solid #39C4BD',
        borderRadius: '50%',
        justifyContent: 'center',
        alignItems: 'center',
        boxShadow: '0 0 0.1rem #39C4BD',
        marginRight: '2rem',
      },
      '& svg': {
        fill: '#39C4BD',
        width: '1.2rem',
        height: '1rem',
        transition: '300ms opacity ease',
        opacity: 0,
      },
      '&[is="selected"]': {
        '&': {
          background: '#ecfefe',
        },
        '& svg': {
          opacity: 1,
        },
      },
    },
  },
  footer: {
    display: 'flex',
    position: 'absolute',
    pointerEvents: 'none',
    bottom: 0,
    left: 0,
    right: 0,
    paddingBottom: '2.2rem',
    justifyContent: 'center',
  },
});

export default memo(Filter);
